import { customEvents } from '../../config/custom-events';
import switchLinksTabIndex from '../../utils/switchLinksTabIndex';
import { breakpoints } from '../../config/breakpoints';
import { config } from './config';
import { showOverlay, hideOverlay } from '../../utils/pageOverlay';
import keyboardFocusCallback from '../../utils/keyboardFocusCallback';

export default function hamburger() {
  /**
   * Main (hamburger) navigation wrapper selector
   * @type {Element|null}
   */
  const mainNavigation = document.querySelector('.js-main-navigation');

  /**
   * Mobile navigation wrapper selector
   */
  const mobileNavigation = document.querySelector('.js-mobile-navigation');

  /**
   * Header selector
   * @type {Element|null}
   */
  const header = document.querySelector(config.header);

  /**
   * Header mobile nav open state modifier class
   * @type {string}
   */
  const headerMobileOpenStateModifierClass = 'header--state-mobile-open';

  /**
   * Header mobile wrap selector
   * @type {string}
   */
  const mobileWrapSelector = '.js-navigation-mobile-wrap';

  /**
   * Mobile navigation links with children selector
   * @type {NodeListOf<Element>}
   */
  const mobileLinksWithChildren = document.querySelectorAll('.js-navigation-mobile-open-child-screen');

  /**
   * Mobile navigation sub-navigation selector
   * @type {string}
   */
  const mobileSubNavSelector = '.js-sub-navigation-mobile';

  /**
   * Mobile navigation sub-navigation collapse selector
   * @type {string}
   */
  const navCollapse = '.js-mobile-nav-collapse';

  /**
   * Mobile navigation sub-navigation open state modifier class
   * @type {string}
   */
  const mobileSubNavOpenStateClass = 'navigation__mobile-nav-sub--state-active';

  /**
   * Mobile navigation back button
   * @type {Element}
   */
  const mobileNavBackButton = document.querySelector('.js-mobile-navigation-back');

  /**
   * Mobile navigation back button visible state modifier class
   * @type {string}
   */
  const mobileNavBackButtonVisibleStateClass = 'navigation__back-button--state-visible';

  /**
   * Mobile navigation breadcrumbs
   */
  const mobileNavBreadcrumbs = document.querySelector('.js-mobile-navigation-breadcrumbs');

  /**
   * Mobile navigation high level link selector
   * @type {Element}
   */
  const mobileNavHighLevelLinks = document.querySelectorAll('.js-navigation-mobile-high-level-link');

  /**
   * is Nav Opened flag
   * @type {boolean}
   */
  let isNavOpened = false;

  /**
   * Initial width holder
   * @type {number}
   */
  let width = window.innerWidth;

  /**
   * Update mobile navigation height & width
   */
  function updateMobileNavigationDimensions() {
    mobileNavigation.style.setProperty('--mobile-nav-height', `${window.innerHeight}px`);
    mobileNavigation.style.setProperty('--mobile-nav-width', `${window.innerWidth}px`);
  }

  /**
   * Resize handler
   */
  function handleResize() {
    if (window.innerWidth !== width) {
      width = window.innerWidth;

      if (isNavOpened) {
        document.dispatchEvent(new CustomEvent(customEvents.navigation.main.close));
      }
      updateMobileNavigationDimensions();
    }
  }

  /**
   * Orientation change handler
   */
  function handleOrientationChange() {
    updateMobileNavigationDimensions();
    if (isNavOpened) {
      document.dispatchEvent(new CustomEvent(customEvents.navigation.main.close));
    }
  }

  /**
   * Hide mobile navigation (first level) for smooth container resize
   */
  function hideFirstLevelMobileNavigation() {
    mobileNavHighLevelLinks.forEach((el) => el.classList.add('navigation__mobile-nav-link--state-hidden'));
  }

  /**
   * Show mobile navigation (first level) after close child level menu
   */
  function showFirstLevelMobileNavigation() {
    mobileNavHighLevelLinks.forEach((el) => el.classList.remove('navigation__mobile-nav-link--state-hidden'));
  }

  /**
   * Toggle mobile back button visibility
   */
  function toggleMobileNavBackButton() {
    mobileNavBackButton.classList.toggle(mobileNavBackButtonVisibleStateClass);
  }

  /**
   * Create mobile navigation breadcrumb item HTML
   * @param {string} name
   * @return {string}
   */
  function createMobileNavBreadcrumbItem(name) {
    return `<span class="navigation__breadcrumb-button">${name}</span>`;
  }

  /**
   * Create mobile navigation breadcrumbs
   */
  function buildMobileNavBreadcrumbs() {
    const defaultBreadcrumb = mobileNavBreadcrumbs.getAttribute('data-default');
    const breadcrumbs = [];
    const activeMenus = document.querySelectorAll(`${mobileSubNavSelector}[data-active="true"]`);

    if (defaultBreadcrumb.length) {
      breadcrumbs.push(createMobileNavBreadcrumbItem(defaultBreadcrumb));
    }

    if (activeMenus) {
      activeMenus.forEach((menu) => {
        breadcrumbs.push(createMobileNavBreadcrumbItem(menu.getAttribute('data-breadcrumb-name')));
      });
    }

    mobileNavBreadcrumbs.innerHTML = breadcrumbs.join('');
  }

  /**
   * Reset mobile navigation to initial state
   */
  function resetMobileNavigation() {
    document.querySelectorAll(`${mobileSubNavSelector}.${mobileSubNavOpenStateClass}`).forEach((subNav) => {
      subNav.classList.remove(mobileSubNavOpenStateClass);
      subNav.setAttribute('data-active', 'false');
    });
    mobileNavBackButton.classList.remove(mobileNavBackButtonVisibleStateClass);
    document.querySelector(mobileWrapSelector).classList.remove('navigation__mobile-wrap--layout-full-width');
    buildMobileNavBreadcrumbs();
    setTimeout(() => {
      showFirstLevelMobileNavigation();
    }, 300);
  }

  document.addEventListener(customEvents.navigation.main.open, () => {
    // ipad or mobile landscape
    if (window.innerWidth >= breakpoints.md) {
      mainNavigation.setAttribute('aria-hidden', 'false');
      mainNavigation.classList.add(config.navigationOpenModifierClass);
      switchLinksTabIndex(mainNavigation, '0', 100);
      showOverlay();
      mainNavigation.querySelector('a').focus();
    } else {
      updateMobileNavigationDimensions();
      mobileNavigation.setAttribute('aria-hidden', 'false');
      mobileNavigation.classList.remove(config.navigationClosedModifierClass);
      mobileNavigation.classList.add(config.navigationOpenModifierClass);
      switchLinksTabIndex(mobileNavigation, '0', 100);
      header.classList.add(headerMobileOpenStateModifierClass);
      document.querySelector('body').classList.add('overflow-hidden');

      const collapses = document.querySelectorAll(navCollapse);
      if (collapses.length) {
        if (collapses[0].getAttribute('data-initialized') !== 'true') {
          import('bootstrap/js/dist/collapse').then((module) => {
            collapses.forEach((collapse) => {
              // eslint-disable-next-line no-new,new-cap
              new module.default(collapse);
              collapse.setAttribute('data-initialized', 'true');
            });
          });
        }
      }
    }
    isNavOpened = true;
    document.querySelector(config.body).classList.add(config.bodyLockScrollClass);
  });

  document.addEventListener(customEvents.navigation.main.close, () => {
    // close all active navs
    [mainNavigation, mobileNavigation].forEach((nav) => {
      if (nav.getAttribute('aria-hidden') === 'false') {
        if (nav === mainNavigation) {
          mainNavigation.setAttribute('aria-hidden', 'true');
          switchLinksTabIndex(mainNavigation, '-1', 100);
          mainNavigation.classList.remove(config.navigationOpenModifierClass);
          hideOverlay();
        } else {
          mobileNavigation.setAttribute('aria-hidden', 'true');
          switchLinksTabIndex(mobileNavigation, '-1', 100);
          mobileNavigation.classList.remove(config.navigationOpenModifierClass);
          mobileNavigation.classList.add(config.navigationClosedModifierClass);
          header.classList.remove(headerMobileOpenStateModifierClass);
          document.querySelector('body').classList.remove('overflow-hidden');

          // under hood, after smooth animations end
          setTimeout(() => {
            resetMobileNavigation();
          }, 300);
        }
      }
    });
    isNavOpened = false;
    document.querySelector(config.body).classList.remove(config.bodyLockScrollClass);
  });

  mobileLinksWithChildren.forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const subNav = link.parentNode.querySelector(mobileSubNavSelector);

      subNav.classList.add(mobileSubNavOpenStateClass);
      subNav.setAttribute('data-active', 'true');

      // set wrapper to full width
      setTimeout(() => {
        document.querySelector(mobileWrapSelector).classList.add('navigation__mobile-wrap--layout-full-width');
      }, 100);

      hideFirstLevelMobileNavigation();
      toggleMobileNavBackButton();
      buildMobileNavBreadcrumbs();
    });
  });

  // mobile nav back button handle
  mobileNavBackButton.addEventListener('click', resetMobileNavigation);

  // keyboard navigation/focus control
  document.addEventListener('DOMContentLoaded', () => {
    const closeMenuKeyboardFocusCallback = () => {
      const navigationToggle = document.querySelector('.js-hamburger');
      if (navigationToggle) {
        setTimeout(() => {
          navigationToggle.focus();
        }, 1);
      }
    };

    keyboardFocusCallback(
      mainNavigation,
      closeMenuKeyboardFocusCallback,
    );
  });

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleOrientationChange);
}
